// src/components/DirectMessageScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import "./DirectMessageScreen.css";

function DirectMessageScreen() {
  const [user] = useAuthState(auth);

  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState("");
  const [activeThread, setActiveThread] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");

  const [searchUserText, setSearchUserText] = useState(""); // start new chat by email/username/name
  const [searchResults, setSearchResults] = useState([]);

  const [err, setErr] = useState("");

  // Load my threads in realtime
  useEffect(() => {
    if (!user) return;

    setErr("");
    const qThreads = query(
      collection(db, "threads"),
      where("participants", "array-contains", user.uid),
      orderBy("lastMessageAt", "desc"),
      limit(50)
    );

    const unsub = onSnapshot(
      qThreads,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setThreads(rows);

        // auto-select first thread if none selected
        if (!activeThreadId && rows.length > 0) {
          setActiveThreadId(rows[0].id);
        }
      },
      (e) => {
        console.error(e);
        setErr("Failed to load threads. If Firestore asks for an index, create it.");
      }
    );

    return () => unsub();
  }, [user, activeThreadId]);

  // Load active thread doc + messages realtime
  useEffect(() => {
    if (!user || !activeThreadId) return;

    setErr("");

    // find thread in local state
    const th = threads.find((t) => t.id === activeThreadId) || null;
    setActiveThread(th);

    const qMsgs = query(
      collection(db, "threads", activeThreadId, "messages"),
      orderBy("createdAt", "asc"),
      limit(200)
    );

    const unsub = onSnapshot(
      qMsgs,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setMessages(rows);
      },
      (e) => {
        console.error(e);
        setErr("Failed to load messages.");
      }
    );

    return () => unsub();
  }, [user, activeThreadId, threads]);

  const myUid = user?.uid;

  const otherParticipant = useMemo(() => {
    if (!activeThread || !myUid) return null;
    const participants = activeThread.participants || [];
    const otherId = participants.find((p) => p !== myUid);
    const emails = activeThread.participantEmails || [];
    const otherEmail =
      emails.find((em) => em !== user?.email) || "Unknown";

    return { otherId, otherEmail };
  }, [activeThread, myUid, user?.email]);

  // Search users to start a new chat (simple client-side search over first 25)
  // If you want stronger search later, we can add indexed fields.
  const handleSearchUsers = async () => {
    if (!user) return;
    setErr("");
    setSearchResults([]);

    const qText = searchUserText.trim();
    if (!qText) return;

    try {
      // Basic: pull a small list and filter locally
      const qUsers = query(collection(db, "users"), limit(50));
      const snap = await getDocs(qUsers);
      const rows = [];
      snap.forEach((d) => {
        const u = { id: d.id, ...d.data() };
        if (u.id === user.uid) return;
        rows.push(u);
      });

      const filtered = rows.filter((u) => {
        const name = (u.displayName || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const username = (u.username || "").toLowerCase();
        const q = qText.toLowerCase();
        return name.includes(q) || email.includes(q) || username.includes(q);
      });

      setSearchResults(filtered.slice(0, 10));
    } catch (e) {
      console.error(e);
      setErr("User search failed. Check your users collection/rules.");
    }
  };

  // Find or create thread between current user and selected user
  const startChatWithUser = async (otherUser) => {
    if (!user) return;
    setErr("");

    try {
      // Try to find an existing thread that contains both participants
      const qExisting = query(
        collection(db, "threads"),
        where("participants", "array-contains", user.uid),
        limit(50)
      );
      const snap = await getDocs(qExisting);

      let existing = null;
      snap.forEach((d) => {
        const data = d.data();
        const parts = data.participants || [];
        if (parts.includes(otherUser.id) && parts.includes(user.uid)) {
          existing = { id: d.id, ...data };
        }
      });

      if (existing) {
        setActiveThreadId(existing.id);
        setSearchResults([]);
        setSearchUserText("");
        return;
      }

      // Create new thread with a deterministic id so duplicates don’t happen
      const ids = [user.uid, otherUser.id].sort();
      const threadId = `dm_${ids[0]}_${ids[1]}`;

      await setDoc(doc(db, "threads", threadId), {
        participants: [user.uid, otherUser.id],
        participantEmails: [user.email || "", otherUser.email || ""],
        lastMessage: "",
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });

      setActiveThreadId(threadId);
      setSearchResults([]);
      setSearchUserText("");
    } catch (e) {
      console.error(e);
      setErr("Could not start chat.");
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setErr("");

    if (!user) return setErr("Please login first.");
    if (!activeThreadId) return setErr("Select a thread first.");
    const text = messageText.trim();
    if (!text) return;

    try {
      // Add message
      await addDoc(collection(db, "threads", activeThreadId, "messages"), {
        senderId: user.uid,
        senderEmail: user.email || "",
        text,
        createdAt: serverTimestamp(),
      });

      // Update thread last message
      await updateDoc(doc(db, "threads", activeThreadId), {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
      });

      setMessageText("");
    } catch (e) {
      console.error(e);
      setErr("Failed to send message.");
    }
  };

  return (
    <div className="dm-container">
      <h1>Direct Messages</h1>

      {err && <p className="dm-error">{err}</p>}

      {/* Start new chat */}
      <div className="dm-newchat">
        <input
          type="text"
          placeholder="Search users by name/email/username…"
          value={searchUserText}
          onChange={(e) => setSearchUserText(e.target.value)}
        />
        <button className="dm-btn" type="button" onClick={handleSearchUsers}>
          Search
        </button>
      </div>

      {searchResults.length > 0 && (
        <div className="dm-searchresults">
          {searchResults.map((u) => (
            <div key={u.id} className="dm-searchrow">
              <div>
                <div className="dm-strong">{u.displayName || u.username || "User"}</div>
                <div className="dm-muted">{u.email || ""}</div>
              </div>
              <button className="dm-btn ghost" onClick={() => startChatWithUser(u)}>
                Message
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="dm-grid">
        {/* Threads list */}
        <div className="dm-left">
          <div className="dm-subtitle">Threads</div>

          {threads.length === 0 ? (
            <p className="dm-muted">No conversations yet.</p>
          ) : (
            <div className="dm-threadlist">
              {threads.map((t) => {
                const isActive = t.id === activeThreadId;
                const parts = t.participantEmails || [];
                const otherEmail = parts.find((em) => em && em !== user?.email) || "Unknown";
                return (
                  <button
                    key={t.id}
                    className={isActive ? "dm-thread active" : "dm-thread"}
                    onClick={() => setActiveThreadId(t.id)}
                    type="button"
                  >
                    <div className="dm-strong">{otherEmail}</div>
                    <div className="dm-muted one-line">{t.lastMessage || "—"}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="dm-right">
          <div className="dm-subtitle">
            {otherParticipant?.otherEmail ? `Chat with ${otherParticipant.otherEmail}` : "Select a thread"}
          </div>

          <div className="dm-messages">
            {messages.length === 0 ? (
              <p className="dm-muted">No messages yet.</p>
            ) : (
              messages.map((m) => {
                const mine = m.senderId === user?.uid;
                return (
                  <div key={m.id} className={mine ? "dm-bubble mine" : "dm-bubble"}>
                    <div className="dm-text">{m.text}</div>
                    <div className="dm-mini">{mine ? "You" : m.senderEmail || "User"}</div>
                  </div>
                );
              })
            )}
          </div>

          <form className="dm-send" onSubmit={handleSend}>
            <input
              type="text"
              placeholder="Type your message…"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              required
              disabled={!activeThreadId}
            />
            <button className="dm-btn primary" type="submit" disabled={!activeThreadId}>
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DirectMessageScreen;
